import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  provideAppInitializer,
} from '@angular/core';
import { provideRouter } from '@angular/router';
import { routes } from './app.routes';
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { BrowserModule } from '@angular/platform-browser';
import {
  TranslateLoader,
  TranslateModule,
  TranslateService,
} from '@ngx-translate/core';
import { createTranslateLoader, initializeApp } from './translate.config';
import { MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { LanguageService } from './features/translations/services/language.service';
import { AuthInterceptorService } from './features/auth/interceptors/auth-interceptor.service';
import { environment } from '../environments/environment';
import { BugsnagErrorHandler } from '@bugsnag/plugin-angular';
import Bugsnag from '@bugsnag/browser';
import { providePrimeNG } from 'primeng/config';
import { definePreset } from '@primeng/themes';
import Aura from '@primeng/themes/aura';
import {
  LoaderInterceptor,
  APP_URLS_CONFIG,
  APPLICATION_NAME,
  ACCESSIBLE_COMPANIES_POLL_INTERVAL,
  ENV_CONFIG_TOKEN,
  SociumApp,
  EnvConfig,
  provideAppStates,
  REFRESH_INTERVAL,
  TOKEN_BUFFER_TIME,
} from '@socium/sociumflow';
import { Applications } from '@socium/common-api';

const ENV_CONFIG: EnvConfig = {
  environment: environment.env,
  appName: SociumApp.DOC,
  apiUrl: environment.workSpaceEndpoint,
  intercomAppId: environment.intercomAppId,
  showIntercom: environment.showIntercom,
};

const SOCIUM_PRESET = definePreset(Aura, {
  semantic: {
    primary: {
      50: '{blue.50}',
      100: '{blue.100}',
      200: '{blue.200}',
      300: '{blue.300}',
      400: '{blue.400}',
      500: '{blue.500}',
      600: '{blue.600}',
      700: '{blue.700}',
      800: '{blue.800}',
      900: '{blue.900}',
      950: '{blue.950}',
    },
    secondary: {
      50: '{red.50}',
      100: '{red.100}',
      200: '{red.200}',
      300: '{red.300}',
      400: '{red.400}',
      500: '{red.500}',
      600: '{red.600}',
      700: '{red.700}',
      800: '{red.800}',
      900: '{red.900}',
      950: '{red.950}',
    },
  },
});

Bugsnag.start({
  apiKey: environment.bugsnagApiKey,
  releaseStage: environment.production ? 'Production' : 'Dev',
  enabledErrorTypes: {
    unhandledExceptions: true,
    unhandledRejections: true,
  },
  context: 'Frontend',
});
/**
 * Angular configuration object that sets up essential providers and modules for the application.
 */
export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes),
    provideHttpClient(withInterceptorsFromDi()),
    providePrimeNG({
      theme: {
        preset: SOCIUM_PRESET,
        options: {
          darkModeSelector: '.dark',
        },
      },
    }),
    importProvidersFrom(
      BrowserAnimationsModule,
      BrowserModule,
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: createTranslateLoader,
          deps: [HttpClient],
        },
      }),
    ),
    MessageService,
    DialogService,
    provideAppInitializer(() => {
      const initializerFn = initializeApp(
        inject(TranslateService),
        inject(LanguageService),
      );
      return initializerFn();
    }),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptorService,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LoaderInterceptor,
      multi: true,
    },
    {
      provide: APP_URLS_CONFIG,
      useValue: {
        WORKSPACE: environment.workspaceUrl,
        JOB: environment.jobUrl,
        WORKFLOW: environment.workflowUrl,
        PERF: environment.perfUrl,
        DOC: environment.docUrl,
        PAYROLL: environment.payrollUrl,
        TIME: environment.timeUrl,
        WORKSPACE_API: environment.workSpaceEndpoint,
        ENV: environment.env,
      },
    },
    { provide: APPLICATION_NAME, useValue: Applications.DOC },
    { provide: ENV_CONFIG_TOKEN, useValue: ENV_CONFIG },
    { provide: REFRESH_INTERVAL, useValue: environment.refreshInterval },
    { provide: TOKEN_BUFFER_TIME, useValue: environment.tokenBufferTime },
    {
      provide: ACCESSIBLE_COMPANIES_POLL_INTERVAL,
      useValue: environment.accessibleCompaniesPollInterval,
    },
    ...provideAppStates(),
    environment.bugsnagEnabled
      ? {
          provide: ErrorHandler,
          useFactory: () => new BugsnagErrorHandler(),
        }
      : [],
  ],
};
